import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ExploreServiceState } from '../redux/exploreServiceSlice';
import ExploreServiceLocation, { PlaceLocation } from '../types/ExploreServiceLocation';
import { ExploreServiceRecordType } from '../types/ExploreServicePayload';
import getAdminExploreServiceFilters from './getAdminExploreServiceFilters';
import useTrailEngagementRecordIds from './useTrailEngagementRecordIds';

type UseGeneralExploreServiceQueryArgsParams = {
  hasTrails?: boolean;
  isPhotospotOverlaySelected?: boolean;
  /**
   * Merged over the defaults this hook sets. Lets a caller ask for a bigger page when it filters results client-side.
   * Like `recordTypesToReturn`, this lands in a dependency array, so hoist or memoize it rather than passing a
   * fresh literal each render — see `CustomRouteOverlays`, which keeps its args object at module scope.
   */
  recordTypeLimits?: Partial<Record<ExploreServiceRecordType, number>>;
  recordTypesToReturn?: ExploreServiceRecordType[];
  shouldFetchParks?: boolean;
};

const useGeneralExploreServiceQueryArgs = ({
  hasTrails = false,
  isPhotospotOverlaySelected = false,
  recordTypeLimits,
  recordTypesToReturn,
  shouldFetchParks = false
}: UseGeneralExploreServiceQueryArgsParams) => {
  const { adminFilters, boundingBox, distanceAwayIsochrone, filters, locationResult, mapRotation, naturalLanguageQuery, sort, trailEngagement } =
    useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);

  const recordIds = useTrailEngagementRecordIds(trailEngagement);
  const filtersWithRecordIds = useMemo(() => {
    if (recordIds) {
      return { ...filters, hasTrails, recordIds };
    } else {
      return { ...filters, hasTrails };
    }
  }, [filters, hasTrails, recordIds]);

  const filtersWithAdminFilters = useMemo(() => {
    const adminSpecificFilters = getAdminExploreServiceFilters(adminFilters);

    if (Object.keys(adminSpecificFilters).length > 0) {
      return { ...filtersWithRecordIds, ...adminSpecificFilters };
    } else {
      return filtersWithRecordIds;
    }
  }, [adminFilters, filtersWithRecordIds]);

  const queryArgs = useMemo(() => {
    const polygon = distanceAwayIsochrone
      ? distanceAwayIsochrone.coordinates[0].map(position => {
          return { lat: position[1], lng: position[0] };
        })
      : undefined;
    const placeLocation: PlaceLocation | undefined =
      locationResult && 'location_type' in locationResult && locationResult.location_type
        ? { id: locationResult.ID, placeType: locationResult.location_type }
        : undefined;

    const recordTypes: ExploreServiceRecordType[] = [
      ...(recordTypesToReturn || (['trail', 'poi'] as ExploreServiceRecordType[])),
      ...(isPhotospotOverlaySelected ? (['photospot'] as ExploreServiceRecordType[]) : []),
      ...(shouldFetchParks ? (['area'] as ExploreServiceRecordType[]) : [])
    ];

    const mergedRecordTypeLimits = { ...(shouldFetchParks ? { area: 20 } : {}), ...recordTypeLimits };

    return {
      filters: filtersWithAdminFilters,
      location: {
        mapRotation,
        ...(boundingBox ?? { bottomLeft: undefined, bottomRight: undefined, topLeft: undefined, topRight: undefined }),
        // Don't send the locationResult when a we have a polygon since the polygon should take priority
        ...(!polygon ? placeLocation : {}),
        ...(polygon ? { polygons: [polygon] } : {})
      } as ExploreServiceLocation,
      query: naturalLanguageQuery,
      recordTypeLimits: Object.keys(mergedRecordTypeLimits).length > 0 ? mergedRecordTypeLimits : undefined,
      recordTypesToReturn: recordTypes,
      sort
    };
  }, [
    boundingBox,
    distanceAwayIsochrone,
    filtersWithAdminFilters,
    isPhotospotOverlaySelected,
    locationResult,
    mapRotation,
    naturalLanguageQuery,
    recordTypeLimits,
    recordTypesToReturn,
    shouldFetchParks,
    sort
  ]);

  return queryArgs;
};

export default useGeneralExploreServiceQueryArgs;
